import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Divider } from '@heroui/react';
import { DatePicker } from '@enfomentis/ui';
import { PlaygroundPageLayout } from '../shared';
import type { DateValue } from '@internationalized/date';

export const DatePickerExamples: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<DateValue | null>(null);
  const [tradingDate, setTradingDate] = useState<DateValue | null>(null);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Basic Date Picker</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <DatePicker
                label="Select Date"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Birth Date"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Event Date"
                description="Choose the event date"
                className="max-w-[284px]"
              />
            </div>
          </CardBody>
        </Card>

        {/* Variants */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Variants</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DatePicker
                label="Flat"
                variant="flat"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Bordered"
                variant="bordered"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Underlined"
                variant="underlined"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Faded"
                variant="faded"
                className="max-w-[284px]"
              />
            </div>
          </CardBody>
        </Card>

        {/* Colors */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Colors</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <DatePicker
                label="Default"
                color="default"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Primary"
                color="primary"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Secondary"
                color="secondary"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Success"
                color="success"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Warning"
                color="warning"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Danger"
                color="danger"
                className="max-w-[284px]"
              />
            </div>
          </CardBody>
        </Card>

        {/* Sizes */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Sizes</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="flex flex-col gap-4">
              <DatePicker
                label="Small"
                size="sm"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Medium (Default)"
                size="md"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Large"
                size="lg"
                className="max-w-[284px]"
              />
            </div>
          </CardBody>
        </Card>

        {/* Time Granularity */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Time Granularity</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <DatePicker
                label="Day Only"
                granularity="day"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Hour Precision"
                granularity="hour"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Minute Precision"
                granularity="minute"
                className="max-w-[284px]"
              />
              <DatePicker
                label="Second Precision"
                granularity="second"
                className="max-w-[284px]"
              />
            </div>
          </CardBody>
        </Card>

        {/* States */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">States</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DatePicker
                label="Disabled"
                isDisabled
                className="max-w-[284px]"
              />
              <DatePicker
                label="Read Only"
                isReadOnly
                className="max-w-[284px]"
              />
              <DatePicker
                label="Required"
                isRequired
                className="max-w-[284px]"
              />
              <DatePicker
                label="Invalid"
                isInvalid
                errorMessage="Please select a valid date"
                className="max-w-[284px]"
              />
            </div>
          </CardBody>
        </Card>

        {/* Trading-Specific Examples */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Trading Journal Use Cases</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trade Entry Form */}
              <div className="space-y-4">
                <h4 className="text-md font-medium">Trade Entry Form</h4>
                <div className="space-y-3">
                  <DatePicker
                    label="Trade Date"
                    description="Date the trade was executed"
                    color="primary"
                    value={tradingDate}
                    onChange={setTradingDate}
                    className="max-w-[284px]"
                    isRequired
                  />
                  <DatePicker
                    label="Settlement Date"
                    description="Expected settlement date"
                    color="secondary"
                    granularity="day"
                    className="max-w-[284px]"
                  />
                  <DatePicker
                    label="Exit Target Date"
                    description="Planned exit timeframe"
                    variant="bordered"
                    className="max-w-[284px]"
                  />
                </div>
              </div>

              {/* Analysis Tools */}
              <div className="space-y-4">
                <h4 className="text-md font-medium">Analysis Tools</h4>
                <div className="space-y-3">
                  <DatePicker
                    label="Report Start Date"
                    description="Beginning of analysis period"
                    color="success"
                    className="max-w-[284px]"
                  />
                  <DatePicker
                    label="Report End Date"
                    description="End of analysis period"
                    color="success"
                    className="max-w-[284px]"
                  />
                  <DatePicker
                    label="Comparison Date"
                    description="Date for performance comparison"
                    variant="faded"
                    className="max-w-[284px]"
                  />
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Controlled Example */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Controlled Date Picker</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="flex flex-col gap-4">
              <DatePicker
                label="Controlled Date"
                value={selectedDate}
                onChange={setSelectedDate}
                className="max-w-[284px]"
                color="primary"
              />
              <div className="text-sm text-foreground-500">
                Selected date: {selectedDate ? selectedDate.toString() : 'None'}
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};